import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
} from '@mui/material';
import { fetchParticipantResponse } from '../controllers/surveysController';
import BackButton from '../components/BackButton';

const QuestionTypeLabels = {
  0: 'Tek Seçim',
  1: 'Çoklu Seçim',
  2: 'Kısa Metin',
  3: 'Uzun Metin',
  4: 'Değerlendirme (1-5)',
  5: 'Tarih',
  6: 'Sayı',
};

const ParticipantTypeLabels = {
  0: 'Üye',
  1: 'Alt Üye',
};

// Helper function to format UTC date without timezone conversion
const formatUTCDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const SurveyParticipantResponse = () => {
  const { id: surveyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const personId = searchParams.get('personId');
  const participantType = searchParams.get('participantType');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!personId || !participantType) {
      setError('Geçersiz parametreler');
      setLoading(false);
      return;
    }

    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchParticipantResponse(surveyId, personId, Number(participantType));
        if (!active) return;
        setResponse(data);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Katılımcı cevapları alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [surveyId, personId, participantType]);

  const renderAnswer = (answer) => {
    const { questionType, selectedOptionTexts, textAnswer, ratingValue, dateAnswer, numberAnswer } = answer;

    if (questionType === 0 || questionType === 1) {
      // Single Choice or Multiple Choice
      if (selectedOptionTexts && selectedOptionTexts.length > 0) {
        return (
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {selectedOptionTexts.map((optText, idx) => (
              <Chip 
                key={idx} 
                label={optText} 
                size="small" 
                sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} 
              />
            ))}
          </Stack>
        );
      }
      return <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>Cevap verilmemiş</Typography>;
    } else if (questionType === 4) {
      // Rating
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#444444' }}>
            {ratingValue !== null && ratingValue !== undefined ? ratingValue : '-'}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#666666' }}>/ 5</Typography>
        </Stack>
      );
    } else if (questionType === 5) {
      // Date
      return (
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
          {dateAnswer ? formatUTCDateTime(dateAnswer) : '-'}
        </Typography>
      );
    } else if (questionType === 6) {
      // Number
      return (
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
          {numberAnswer !== null && numberAnswer !== undefined ? numberAnswer : '-'}
        </Typography>
      );
    } else {
      // Text (questionType === 2) or LongText (questionType === 3)
      return (
        <Typography sx={{ fontSize: '14px', color: '#444444', whiteSpace: 'pre-wrap' }}>
          {textAnswer || '-'}
        </Typography>
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2, fontSize: '14px' }}>{error}</Alert>
        <BackButton onClick={() => navigate(`/surveys/${surveyId}/responses`)} label="Geri Dön" />
      </Box>
    );
  }

  if (!response) return null;

  return (
    <Stack spacing={3}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <BackButton onClick={() => navigate(`/surveys/${surveyId}/responses`)} />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>
          {response.surveyTitle} - Katılımcı Cevapları
        </Typography>
      </Stack>

      <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', p: 2.5 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip 
              label={ParticipantTypeLabels[response.participantType] || 'Bilinmeyen'} 
              sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} 
            />
            {response.parentMemberId && (
              <Chip 
                label={`Üst Üye: ${response.parentMemberId}`} 
                sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#fafafa', color: '#666666' }} 
              />
            )}
            <Chip 
              label={`Gönderilme: ${formatUTCDateTime(response.submittedAtUtc)}`} 
              sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#fafafa', color: '#666666' }} 
            />
            {response.updatedAtUtc && (
              <Chip 
                label={`Güncellenme: ${formatUTCDateTime(response.updatedAtUtc)}`} 
                sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#fafafa', color: '#666666' }} 
              />
            )}
          </Stack>
        </Stack>
      </Card>

      {response.answers && response.answers.length > 0 ? (
        <Stack spacing={2}>
          {response.answers.map((answer, index) => (
            <Card 
              key={answer.questionId || index} 
              sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip 
                      label={`Soru ${index + 1}`} 
                      sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#0E4D94', color: '#ffffff' }} 
                    />
                    <Chip 
                      label={QuestionTypeLabels[answer.questionType] || 'Bilinmeyen'} 
                      sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#E7EDF4', color: '#0E4D94' }} 
                    />
                  </Stack>
                  <Divider sx={{ borderColor: '#e6e6e6' }} />
                  <Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#444444', mb: 1.5 }}>
                      {answer.questionText}
                    </Typography>
                    <Card 
                      sx={{ 
                        border: '1px solid #e6e6e6', 
                        boxShadow: 'none', 
                        borderRadius: '8px', 
                        p: 2, 
                        backgroundColor: '#fafafa' 
                      }}
                    >
                      {renderAnswer(answer)}
                    </Card>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Alert severity="info" sx={{ fontSize: '14px' }}>Bu katılımcı henüz cevap vermemiş.</Alert>
      )}
    </Stack>
  );
};

export default SurveyParticipantResponse;

